import React, { useEffect, useMemo, useState } from 'react';
import { Button, Card, Input, message, Space, Tabs } from 'antd';
import { SERVICE_TYPES } from './ServiceTypeButtons';
import { SERVICE_TYPE_MAP } from '../config/service-type-map';
import orderStatusOptionsService from '../services/order-status-options';
import { DEFAULT_STATUS_OPTIONS_RAW_MAP, RawStatusOptionsMap } from '../config/status-options';

const SERVICE_LABELS = SERVICE_TYPES.reduce<Record<string, string>>((accumulator, service) => {
  accumulator[service.value] = service.label;
  return accumulator;
}, {});

const DEFAULT_SUB_SERVICE_LABELS: Record<string, string> = {
  _default: '默认',
};

function format_sub_service_label(service: string, subService: string): string {
  if (DEFAULT_SUB_SERVICE_LABELS[subService]) {
    return DEFAULT_SUB_SERVICE_LABELS[subService];
  }

  return SERVICE_TYPE_MAP[service]?.[subService] || subService;
}

function map_to_editor_text(statuses: string[]): string {
  return statuses.join('\n');
}

function editor_text_to_statuses(text: string): string[] {
  const nextStatuses: string[] = [];

  text.split('\n').forEach((line) => {
    const trimmed = line.trim();

    if (trimmed && !nextStatuses.includes(trimmed)) {
      nextStatuses.push(trimmed);
    }
  });

  return nextStatuses;
}

/**
 * 订单状态选项后台配置组件。
 */
const OrderStatusOptionsSettings: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeService, setActiveService] = useState('trademark');
  const [activeSubService, setActiveSubService] = useState('');
  const [rawMap, setRawMap] = useState<RawStatusOptionsMap>(DEFAULT_STATUS_OPTIONS_RAW_MAP);
  const [defaultRawMap, setDefaultRawMap] = useState<RawStatusOptionsMap>(DEFAULT_STATUS_OPTIONS_RAW_MAP);
  const [editorValue, setEditorValue] = useState('');

  useEffect(() => {
    setLoading(true);
    orderStatusOptionsService.get(true).then((payload) => {
      setRawMap(payload.raw_map);
      setDefaultRawMap(payload.default_raw_map);
    }).finally(() => {
      setLoading(false);
    });
  }, []);

  const serviceKeys = useMemo(() => {
    return Object.keys(rawMap);
  }, [rawMap]);

  const currentSubServiceKeys = useMemo(() => {
    const serviceMap = rawMap[activeService] || {};
    return Object.keys(serviceMap);
  }, [rawMap, activeService]);

  useEffect(() => {
    if (!serviceKeys.includes(activeService)) {
      setActiveService(serviceKeys[0] || 'trademark');
    }
  }, [serviceKeys, activeService]);

  useEffect(() => {
    if (!currentSubServiceKeys.includes(activeSubService)) {
      setActiveSubService(currentSubServiceKeys[0] || '');
    }
  }, [currentSubServiceKeys, activeSubService]);

  useEffect(() => {
    const statuses = rawMap[activeService]?.[activeSubService] || [];
    setEditorValue(map_to_editor_text(statuses));
  }, [rawMap, activeService, activeSubService]);

  const handle_save = async () => {
    const nextStatuses = editor_text_to_statuses(editorValue);

    if (!activeService || !activeSubService) {
      message.warning('请选择业务类型和子业务');
      return;
    }

    if (nextStatuses.length === 0) {
      message.warning('至少保留一个状态');
      return;
    }

    const nextRawMap: RawStatusOptionsMap = {
      ...rawMap,
      [activeService]: {
        ...rawMap[activeService],
        [activeSubService]: nextStatuses,
      },
    };

    try {
      setSaving(true);
      const payload = await orderStatusOptionsService.update(nextRawMap);
      setRawMap(payload.raw_map);
      setDefaultRawMap(payload.default_raw_map);
      message.success('订单状态配置保存成功');
    } catch (error: any) {
      message.error(error?.response?.data?.message || '订单状态配置保存失败');
    } finally {
      setSaving(false);
    }
  };

  const handle_restore_current_default = () => {
    const defaultStatuses = defaultRawMap[activeService]?.[activeSubService];

    if (!defaultStatuses) {
      message.warning('当前项没有默认配置');
      return;
    }

    setEditorValue(map_to_editor_text(defaultStatuses));
  };

  const serviceTabItems = serviceKeys.map((serviceKey) => ({
    key: serviceKey,
    label: SERVICE_LABELS[serviceKey] || serviceKey,
  }));

  const subServiceTabItems = currentSubServiceKeys.map((subServiceKey) => ({
    key: subServiceKey,
    label: format_sub_service_label(activeService, subServiceKey),
  }));

  return (
    <Card loading={loading}>
      <Space direction="vertical" size={16} style={{ width: '100%' }}>

        <Tabs
          activeKey={activeService}
          items={serviceTabItems}
          onChange={setActiveService}
          tabBarGutter={16}
        />

        <Tabs
          activeKey={activeSubService}
          items={subServiceTabItems}
          onChange={setActiveSubService}
          tabBarGutter={16}
        />

        <Input.TextArea
          rows={14}
          value={editorValue}
          onChange={(event) => setEditorValue(event.target.value)}
          placeholder="每行输入一个状态"
        />

        <Space>
          <Button onClick={handle_restore_current_default}>
            恢复当前默认值
          </Button>
          <Button type="primary" loading={saving} onClick={handle_save}>
            保存配置
          </Button>
        </Space>
      </Space>
    </Card>
  );
};

export default OrderStatusOptionsSettings;
